import { IAdServerTargeting } from '../../../typings/IPrebid'
import WebModel from '../../models/WebModel'

// PP helps trick DFP into running Mediavine Direct ads when the
// bid is lower than the MVD campaign price
export function calculatePricePriorityTargeting(
  targeting: IAdServerTargeting | undefined
): Array<string> | undefined {
  if (!targeting) {
    return undefined
  }
  const maxPp: number = Math.floor(parseFloat(targeting.hb_pb as string))
  const pp: Array<string> = []

  if (maxPp > 1) {
    for (let i = 1; i <= maxPp; i++) {
      pp.push(`gt${i}`)
    }
  }

  return pp
}

export const maxFirstLookFloor = 200
export const minFirstLookFloor = 4

/* tslint:disable:no-magic-numbers */
export function calculateFirstLookFloor(
  targeting: IAdServerTargeting | undefined
): string | undefined {
  if (!targeting) {
    return undefined
  }
  const bid: number = parseFloat(targeting.hb_pb as string)
  let flp = minFirstLookFloor

  if (bid > 50.0) {
    // Ridiculous $200 from 100+
    flp = maxFirstLookFloor
  } else if (bid > 2) {
    // Every $1 from 4-100
    flp = Math.ceil(bid * 2)
  }

  return flp.toString()
}
/* tslint:enable:no-magic-numbers */

/**
 * The open market floor sets the minimum CPM
 * required to win in the AdX (secondary/GAM/DFP) auction.
 *
 * The value is rounded up into brackets that match
 * line items in DFP. For instance, if we targeted a line
 * item using a CPM of 2.0349348, we wouldn't match any.
 * Instead, we round that CPM up to 2.05, which has a matching
 * line item. The increments of the buckets get wider as the
 * values increase.
 *
 * We try to intelligently manipulate this floor
 * to maximize the second auction bids' CPM.
 */
// Inflates actual bid slightly and rounds it to a targeting "bracket"
// used in DFP.
// The spread between brackets becomes wider as the bid goes up.
export function calculateOpenMarketFloor(
  targeting: IAdServerTargeting | undefined,
  hbDelta: number,
  model: WebModel
): string | undefined {
  if (!targeting) {
    return undefined
  }
  const bid = parseFloat(targeting.hb_pb as string)

  if (!bid) {
    return undefined
  }

  const baseBidMultiplier = 1.05
  const maxTightAuctionHbDelta = 50
  /**
   * We suspect Google *might* cheat a little in the AdX
   * auction, so we start by inflating our header bid by
   *  the baseBidMultiplier.
   */

  let omp = parseFloat((bid * baseBidMultiplier).toFixed(2))

  /**
   * If the header auction had two very competitive bids,
   * we consider the slot's value to be higher and multiply it by
   * a larger factor.
   *
   * The factor is controlled externally in an experiment.
   */

  if (hbDelta < maxTightAuctionHbDelta) {
    omp = parseFloat((bid * model.ompMultiplierForHbDelta).toFixed(2))
  }

  return bucketOmp(omp)
}

export function bucketOmp(omp: number): string {
  /* eslint-disable no-magic-numbers */
  if (omp >= 20.0 && omp <= 100.0) {
    // Round OMP down to near $1.00
    omp = Math.floor(omp) * 100
  } else if (omp >= 5.0) {
    // Every 0.10 from 5-20
    omp = (Math.floor(omp * 10) / 10) * 100
  } else {
    // Every 0.05 from 0.01 - 5.00
    omp = (Math.floor(omp * 20) / 20) * 100
  }

  return Math.round(omp).toString()
}
